/**
 * ModuleActivationContext.jsx — Phase 2 App Shell (Jul 2026)
 *
 * Single source of truth for `is this Phase-1 module active for the
 * currently-scoped company?` across the app shell.
 *
 * Provided at the top of DashboardLayout. Consumed by:
 *   - AppSidebar    → filter items
 *   - CommandPalette → filter results
 *   - ModuleGuard    → render EmptyState when route belongs to a
 *                     deactivated module
 *
 * Refetches on the `company-modules-updated` window event dispatched by
 * the Super Admin `/companies/:id` Modules tab after Save/Reset.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';

const ModuleActivationContext = createContext({
  loading:         true,
  companyId:       null,
  catalogGroups:   [],
  activeKeys:      new Set(),
  inactiveKeys:    new Set(),
  inactiveRoutes:  new Set(),
  isModuleActive:  () => true,
  isRouteActive:   () => true,
  moduleForRoute:  () => null,
  refetch:         () => {},
});

/**
 * Provider. `companyId` may be null when a platform-role user
 * (SuperAdmin / SuperAccountant / SuperSupport) has not selected a
 * tenant — in that case every module is treated as active (fail-open).
 */
export function ModuleActivationProvider({ companyId, isSuperAdmin, children }) {
  const [loading, setLoading]       = useState(true);
  const [catalogGroups, setGroups]  = useState([]);
  const [activeKeys, setActive]     = useState(new Set());
  const [inactiveRoutes, setRoutes] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Kick both requests in parallel. The catalog is cheap + almost-static;
      // the company/modules response is the source of truth for is_active.
      const [catalogRes, companyRes] = await Promise.all([
        api.get('/modules/catalog').catch(() => null),
        companyId ? api.get(`/companies/${companyId}/modules`).catch(() => null) : Promise.resolve(null),
      ]);
      const cat = catalogRes?.data?.groups || [];
      setGroups(Array.isArray(cat) ? cat : []);

      if (companyRes?.data?.groups) {
        const active = new Set();
        const inactRoutes = new Set();
        for (const g of companyRes.data.groups) {
          for (const m of (g.modules || [])) {
            if (m.is_active) active.add(m.key);
            else if (m.route) inactRoutes.add(m.route);
          }
        }
        setActive(active);
        setRoutes(inactRoutes);
      } else {
        // No tenant scope OR fetch failed → fail open (every module counts as active).
        const all = new Set();
        for (const g of cat) for (const m of (g.modules || [])) all.add(m.key);
        setActive(all);
        setRoutes(new Set());
      }
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Refetch when SA toggles modules elsewhere.
  useEffect(() => {
    const onUpdated = (e) => {
      if (!e?.detail?.companyId || Number(e.detail.companyId) === Number(companyId)) load();
    };
    window.addEventListener('company-modules-updated', onUpdated);
    return () => window.removeEventListener('company-modules-updated', onUpdated);
  }, [companyId, load]);

  const value = useMemo(() => {
    const inactiveKeys = new Set();
    for (const g of catalogGroups) for (const m of (g.modules || [])) {
      if (!activeKeys.has(m.key)) inactiveKeys.add(m.key);
    }
    const moduleForRoute = (path) => {
      if (!path) return null;
      for (const g of catalogGroups) for (const m of (g.modules || [])) {
        if (!m.route) continue;
        if (path === m.route || path.startsWith(m.route + '/') || path.startsWith(m.route + '?')) return m;
      }
      return null;
    };
    return {
      loading,
      companyId: companyId ?? null,
      catalogGroups,
      activeKeys,
      inactiveKeys,
      inactiveRoutes,
      isModuleActive: (key) => (isSuperAdmin ? true : activeKeys.has(key)),
      isRouteActive:  (path) => {
        if (isSuperAdmin) return true;
        for (const r of inactiveRoutes) {
          if (!r) continue;
          if (path === r || path.startsWith(r + '/') || path.startsWith(r + '?')) return false;
        }
        return true;
      },
      moduleForRoute,
      refetch: load,
    };
  }, [loading, companyId, catalogGroups, activeKeys, inactiveRoutes, isSuperAdmin, load]);

  return (
    <ModuleActivationContext.Provider value={value}>
      {children}
    </ModuleActivationContext.Provider>
  );
}

export function useModuleActivation() {
  return useContext(ModuleActivationContext);
}

export default ModuleActivationContext;
